import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as z from 'zod';
import useAPI from '../../../hooks/useAPI';
import { UsersEntity } from '../../../lib/types/UserManagement';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import FormError from '../../ui/FormError';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import AuthContext from '../../../contexts/AuthContext';
import MultipleUser from './Multipleuser';

type AddUserModalProps = {
  open: boolean;
  onClose: () => void;
  editData?: UsersEntity | null;
  setEditData?: Dispatch<SetStateAction<UsersEntity | null>>;
  refetch?: () => Promise<void>;
};

type UserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  orgName?: string;
  employeeCode?: string;
};

const schema = z
  .object({
    firstName: z
      .string()
      .nonempty('First name is required')
      .max(50, 'First Name cannot exceed 50 characters.')
      .regex(
        /^[A-Za-z\s]+$/,
        'First Name can only contain letters and spaces.',
      ),
    lastName: z
      .string()
      .nonempty('Last Name is required')
      .max(50, 'Last Name cannot exceed 50 characters.')
      .regex(/^[A-Za-z\s]+$/, 'Last Name can only contain letters and spaces.'),
    email: z
      .string()
      .nonempty('Email ID is required')
      .email('Please enter a valid email address')
      .max(100, 'Email ID cannot exceed 100 characters.')
      .regex(
        /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/,
        'Please enter a valid email address.',
      ),
    employeeCode: z.string().optional().nullable(),
    orgName: z.string().optional().nullable(),
    role: z.string().nonempty('Role is required to add a user'),
  })
  .superRefine((data, ctx) => {
    if (data.role !== 'VENDOR') {
      if (!data.orgName) {
        ctx.addIssue({
          path: ['orgName'],
          code: z.ZodIssueCode.custom,
          message: 'Organization Name is required',
        });
      }
      if (data.role !== 'ORGANIZATION') {
        if (!data.employeeCode) {
          ctx.addIssue({
            path: ['employeeCode'],
            code: z.ZodIssueCode.custom,
            message: 'Employee Code is required',
          });
        }
      }
    }
  });

const AddUserModal = ({
  onClose,
  open,
  editData,
  setEditData,
  refetch: refetchUsers,
}: AddUserModalProps) => {
  const [isConfirm, setIsConfirm] = useState(false);
  const [organizations, setOrganizations] = useState<
    { id: string; name: string }[]
  >([]);
  const { userData } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState<'single' | 'multiple'>('single');

  const orgname = userData?.orgName?.name || 'Organization Name';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<UserPayload>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      role: userData?.role === 'ORGANIZATION' ? 'EMPLOYEE' : 'ORGANIZATION',
    },
  });

  useEffect(() => {
    if (editData) {
      setValue('firstName', editData?.firstName);
      setValue('lastName', editData?.lastName);
      setValue('email', editData?.email);
      setValue('employeeCode', editData?.employeeCode);
      setValue('orgName', editData?.orgName?.id);
      setValue('role', editData?.role);
    }
    if (userData?.role === 'ORGANIZATION') {
      setValue('orgName', userData?.orgName?.id || '');
    }
  }, [editData]);

  const { refetch, isLoading } = useAPI({
    url: `/auth/user/create`,
    method: 'POST',
    onSuccess: () => {
      reset();
      onClose();
      setIsConfirm(true);
      setEditData && setEditData(null);
      refetchUsers && refetchUsers();
    },
    onError: error => {
      toast.error(error?.response?.data?.message);
    },
  });

  const { refetch: refetchUpdate, isLoading: isLoadingUpdate } = useAPI({
    url: `/auth/user/update/${editData?.id}`,
    method: 'PUT',
    onSuccess: data => {
      reset();
      onClose();
      setIsConfirm(true);
      setEditData && setEditData(null);
      refetchUsers && refetchUsers();
    },
    onError: error => {
      toast.error(error?.response?.data?.message);
    },
  });

  const { refetch: fetchOrganizations, isLoading: isOrgLoading } = useAPI({
    url: `${process.env.REACT_APP_API_BASE_URL}/organization/list`,
    method: 'GET',
    onSuccess: (data: any) => {
      if (Array.isArray(data?.result)) {
        setOrganizations(
          data.result.map((org: any) => ({
            id: org.id,
            name: org.name,
          })),
        );
      }
    },
    onError: () => {
      setOrganizations([]);
    },
  });

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const onSubmit = async () => {
    const payload: UserPayload = {
      firstName: watch('firstName'),
      lastName: watch('lastName'),
      email: watch('email'),
      role: watch('role'),
    };

    if (watch('role') !== 'VENDOR') {
      payload.orgName = watch('orgName');
      if (watch('role') !== 'ORGANIZATION') {
        payload.employeeCode = watch('employeeCode');
      }
    }

    if (editData) {
      await refetchUpdate(payload);
    } else {
      await refetch(payload);
    }
  };

  const selectedRole = watch('role');

  const handleCloseModal = () => {
    onClose();
    reset();
    setActiveTab('single');
  };

  return (
    <>
      <Modal
        open={open}
        title={editData ? 'Edit User' : 'Add User'}
        onClose={() => {
          handleCloseModal();
        }}
        dialogClassName={styles.dialog_content}
      >
        {userData?.role === 'ORGANIZATION' && !editData && (
          <div className={styles.tabContainer}>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'single' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('single')}
            >
              Single
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${activeTab === 'multiple' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('multiple')}
            >
              Multiple
            </button>
          </div>
        )}
        {(activeTab === 'single' || userData?.role !== 'ORGANIZATION') && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.addUserWrapper}>
              <div>
                <Form.Group className={styles.selectGroup}>
                  <Form.Label className={styles.selectLabel}>
                    Role <span className={styles.required}>*</span>
                  </Form.Label>
                  <Form.Select
                    className={styles.selectBox}
                    {...register('role')}
                    // defaultValue={
                    //   userData?.role === 'ORGANIZATION'
                    //     ? 'EMPLOYEE'
                    //     : 'ORGANIZATION'
                    // }
                    aria-placeholder="Select Role"
                    disabled={userData?.role === 'ORGANIZATION'}
                  >
                    <option value="" disabled>
                      Select Role
                    </option>
                    <option value="EMPLOYEE">EMPLOYEE</option>
                    {/* <option value="FINANCETEAM">FINANCE TEAM</option> */}
                    <option value="ORGANIZATION">ORGANIZATION</option>
                    {/* <option value="TOKENMANAGER">TOKEN MANAGER</option> */}
                    {/* <option value="VIEWER">VIEWER</option> */}
                    {userData?.role === 'ADMIN' && (
                      <option value="VENDOR">VENDOR</option>
                    )}
                  </Form.Select>
                </Form.Group>
                {errors.role && (
                  <FormError message={errors.role.message as string} />
                )}
              </div>
              {selectedRole !== 'VENDOR' && (
                <Form.Group className={styles.selectGroup}>
                  <Form.Label className={styles.selectLabel}>
                    Organization Name <span className={styles.required}>*</span>
                  </Form.Label>
                  <Form.Select
                    className={styles.selectBox}
                    {...register('orgName')}
                    aria-placeholder="Select Organization"
                    defaultValue={
                      userData?.role == 'ORGANIZATION'
                        ? userData?.orgName?.id
                        : ''
                    }
                    disabled={userData?.role === 'ORGANIZATION'}
                  >
                    <option value="" disabled>
                      Select Organization
                    </option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.orgName && (
                    <FormError message={errors.orgName.message as string} />
                  )}
                </Form.Group>
              )}
              <div className={styles.nameInputs}>
                <Input
                  label="First Name"
                  placeholder="Enter Name"
                  isRequired={true}
                  {...register('firstName')}
                  error={errors.firstName?.message as string}
                  className={styles.nameText}
                />
                <Input
                  label="Last Name"
                  placeholder="Enter Last Name"
                  isRequired={true}
                  {...register('lastName')}
                  error={errors.lastName?.message as string}
                  className={styles.nameText}
                />
              </div>

              <Input
                label="Email Id"
                placeholder="Enter Email"
                isRequired={true}
                {...register('email')}
                error={errors.email?.message as string}
              />

              {selectedRole !== 'VENDOR' && selectedRole !== 'ORGANIZATION' && (
                <>
                  <Input
                    label="Employee Code"
                    placeholder="Enter Employee Code"
                    isRequired={true}
                    {...register('employeeCode')}
                    error={errors.employeeCode?.message as string}
                  />
                </>
              )}
            </div>

            <div className={styles.actions}>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onClose();
                  reset();
                  setActiveTab('single');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="dark"
                type="submit"
                disabled={isLoading || isLoadingUpdate}
                className={styles.delete_btn}
              >
                {editData ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        )}

        {userData?.role === 'ORGANIZATION' && activeTab === 'multiple' && (
          <div className={styles.multipleUserContainer}>
            <MultipleUser
              onClose={onClose}
              organizations={orgname}
              setActiveTab={setActiveTab}
            />
          </div>
        )}
      </Modal>
      <TransactionSuccessModal
        open={isConfirm}
        titleClassName={styles.title}
        title="User added/updated successfully!"
        body={
          <p className={styles.txnInformation}>
            {moment().format('D MMM YYYY HH:mm:ss')}
          </p>
        }
        isCloseIconVisible={true}
        onClose={() => setIsConfirm(false)}
      />
    </>
  );
};

export default AddUserModal;
